import { useCallback, useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Box, Heading, List, Page, Text, TextButton } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';
import { useInfiniteQuery } from '@zg-rentals/react-query5';

import {
  RentalManagerUserPropertyListingsWebApiHandler_applications,
  RentalManagerUserPropertyListingsWebApiHandler_leases,
  RentalManagerUserPropertyListingsWebApiHandler_payments,
  RentalManagerUserPropertyListingsWebApiHandler_statuses,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { useFilterOptions } from '../../components/SelectPropertyPage/hooks';

import PropertiesConstants from '../../constants/PropertiesConstants';
import SelectPropertyConstants from '../../constants/SelectPropertyConstants';
import { ROUTES } from '../../constants/urls';

import InfiniteScroller from '../../components/SelectPropertyPage/InfiniteScroller';
import { PropertyCardSkeleton } from '../../components/SelectPropertyPage/PropertyCardSkeleton';
import SelectPropertyCard from '../../components/SelectPropertyPage/SelectPropertyCard';
import SubHeadingSection from '../../components/SelectPropertyPage/SubHeadingSection';

import type { SelectPropertyListing } from '../../components/SelectPropertyPage/types';

const FILTER_TYPES = PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE;
const PAGINATION_LIMIT = 15;

type SelectPropertyType = (typeof SelectPropertyConstants)[keyof typeof SelectPropertyConstants];
type Pager = { endKey?: string; hasMore?: boolean };
type EligibilityDto = { isEligible?: boolean; title?: string; description?: string };

const SelectPropertyPage = () => {
  const router = useRouter();

  const rawType = router.isReady ? (router.query.type as string) : undefined;
  const validTypes = Object.values(SelectPropertyConstants) as Array<string>;
  const isInvalidType = rawType !== undefined && !validTypes.includes(rawType);

  const type = (
    router.isReady
      ? ((router.query.type as string) ?? SelectPropertyConstants.LISTINGS)
      : SelectPropertyConstants.LISTINGS
  ) as SelectPropertyType;
  const filterTab = router.isReady ? ((router.query.filterTab as string) ?? FILTER_TYPES.ALL) : FILTER_TYPES.ALL;
  const searchText = router.isReady ? ((router.query.searchText as string) ?? '') : '';

  const isBlockingHandleSearchRef = useRef(true);
  const [filterOptions, setFilterOptions] = useFilterOptions();

  const isQueryEnabled = router.isReady;
  const commonMeta = { context: { api: 'rental-manager', details: 'getUserPropertyListings' } };

  const currentFilterOption = filterOptions.options[filterTab] ?? filterOptions.options[FILTER_TYPES.ALL];

  const baseQueryParams = {
    includeActive: currentFilterOption.includeActive,
    includeInactive: currentFilterOption.includeInactive,
    limit: PAGINATION_LIMIT,
    searchText,
  };

  const statusesQuery = useInfiniteQuery({
    queryKey: ['select-property-listings', baseQueryParams],
    queryFn: ({ pageParam }) =>
      RentalManagerUserPropertyListingsWebApiHandler_statuses({
        ...baseQueryParams,
        startKeyExclusive: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const pager = lastPage.pager as Pager;
      return pager.hasMore ? pager.endKey : undefined;
    },
    enabled: isQueryEnabled && type === SelectPropertyConstants.LISTINGS,
    meta: { tags: ['select-property-listings'], ...commonMeta },
  });

  const applicationsQuery = useInfiniteQuery({
    queryKey: ['select-property-applications', baseQueryParams],
    queryFn: ({ pageParam }) =>
      RentalManagerUserPropertyListingsWebApiHandler_applications({
        ...baseQueryParams,
        startKeyExclusive: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const pager = lastPage.pager as Pager;
      return pager.hasMore ? pager.endKey : undefined;
    },
    enabled: isQueryEnabled && type === SelectPropertyConstants.APPLICATIONS,
    meta: { tags: ['select-property-applications'], ...commonMeta },
  });

  const leasesQuery = useInfiniteQuery({
    queryKey: ['select-property-leases', baseQueryParams],
    queryFn: ({ pageParam }) =>
      RentalManagerUserPropertyListingsWebApiHandler_leases({
        ...baseQueryParams,
        startKeyExclusive: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const pager = lastPage.pager as Pager;
      return pager.hasMore ? pager.endKey : undefined;
    },
    enabled: isQueryEnabled && type === SelectPropertyConstants.LEASES,
    meta: { tags: ['select-property-leases'], ...commonMeta },
  });

  const paymentsQuery = useInfiniteQuery({
    queryKey: ['select-property-payments', baseQueryParams],
    queryFn: ({ pageParam }) =>
      RentalManagerUserPropertyListingsWebApiHandler_payments({
        ...baseQueryParams,
        startKeyExclusive: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const pager = lastPage.pager as Pager;
      return pager.hasMore ? pager.endKey : undefined;
    },
    enabled: isQueryEnabled && type === SelectPropertyConstants.PAYMENTS,
    meta: { tags: ['select-property-payments'], ...commonMeta },
  });

  const activeQuery = (() => {
    switch (type) {
      case SelectPropertyConstants.APPLICATIONS:
        return applicationsQuery;
      case SelectPropertyConstants.LEASES:
        return leasesQuery;
      case SelectPropertyConstants.PAYMENTS:
        return paymentsQuery;
      default:
        return statusesQuery;
    }
  })();

  const propertyList: Array<SelectPropertyListing> = useMemo(
    () =>
      activeQuery.data?.pages.flatMap((page) =>
        page.listings.map((item) => {
          const eligibility = item.eligibility as EligibilityDto;
          return {
            listingAlias: item.listingAlias,
            city: item.city,
            state: item.state,
            street: item.street,
            unit: item.unit,
            zip: item.zip,
            message: item.message,
            primaryPhotoUrl: item.primaryPhotoUrl,
            urlPath: item.urlPath,
            eligibility: {
              isEligible: eligibility.isEligible ?? true,
              title: eligibility.title ?? '',
              description: eligibility.description ?? '',
            },
          };
        }),
      ) ?? [],
    [activeQuery.data?.pages],
  );

  useEffect(() => {
    if (isInvalidType) {
      router.replace(ROUTES.PROPERTIES);
    }
  }, [isInvalidType, router]);

  // Redirect to properties page if no listings exist or if the API call failed
  const shouldRedirect =
    activeQuery.isFetched &&
    !activeQuery.isFetching &&
    (activeQuery.data?.pages[0]?.listingCount?.total === 0 || activeQuery.isError);

  useEffect(() => {
    if (shouldRedirect) {
      router.replace({
        pathname: ROUTES.PROPERTIES,
        query: { postingPath: 'true' },
      });
    }
  }, [shouldRedirect, router]);

  // Keep showing loading state until we know if redirect is needed
  const isLoading =
    !activeQuery.isFetched || (activeQuery.isFetching && !activeQuery.isFetchingNextPage) || shouldRedirect;

  // Update filter option counts from the first page listing count
  useEffect(() => {
    const listingCount = activeQuery.data?.pages[0]?.listingCount;
    if (!listingCount) return;

    setFilterOptions((prev) => {
      const updated = { ...prev, options: { ...prev.options } };
      updated.options[FILTER_TYPES.ALL] = { ...updated.options[FILTER_TYPES.ALL], count: listingCount.total };
      updated.options[FILTER_TYPES.ON_MARKET] = {
        ...updated.options[FILTER_TYPES.ON_MARKET],
        count: listingCount.active,
      };
      updated.options[FILTER_TYPES.OFF_MARKET] = {
        ...updated.options[FILTER_TYPES.OFF_MARKET],
        count: listingCount.inactive,
      };
      return updated;
    });
  }, [activeQuery.data?.pages, setFilterOptions]);

  const handleFilterTabClick = useCallback(
    (selectedFilterOption?: (typeof PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS)[number]) => {
      if (!selectedFilterOption) return;
      router.replace(
        { pathname: router.pathname, query: { ...router.query, filterTab: selectedFilterOption.id } },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  // Automatically fired after mount by SearchBox — use flag to block unintended initial API call
  const handleSearch = useCallback(
    (newSearchText?: string) => {
      if (isBlockingHandleSearchRef.current) {
        isBlockingHandleSearchRef.current = false;
        return;
      }
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, searchText: newSearchText ?? '', filterTab },
        },
        undefined,
        { shallow: true },
      );
    },
    [router, filterTab],
  );

  const handleLoadMoreClick = useCallback(() => {
    activeQuery.fetchNextPage();
  }, [activeQuery]);

  const renderPropertyList = () => {
    const propertyCardList = propertyList.map((listing) => (
      <List.Item key={listing.listingAlias}>
        <SelectPropertyCard type={type} listing={listing} />
      </List.Item>
    ));

    return (
      <List.Root
        appearance="none"
        css={{
          display: 'grid',
          gap: '12px 24px',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gridAutoRows: '1fr',
        }}
      >
        {propertyCardList}
      </List.Root>
    );
  };

  const renderLoadMoreBtn = () => {
    const pager = activeQuery.data?.pages.at(-1)?.pager as Pager | undefined;

    return (
      <Box css={{ marginInline: '{spacing.-200}', paddingBlock: '200', clear: 'both' }}>
        <InfiniteScroller
          pager={{
            endKey: pager?.endKey ?? '',
            hasMore: pager?.hasMore ?? false,
          }}
          text="Load more properties"
          onLoadMoreClick={handleLoadMoreClick}
          isLoading={activeQuery.isFetchingNextPage}
        />
      </Box>
    );
  };

  const typeDisplay = `managing ${type}`;
  const propertyPath = `${ROUTES.PROPERTIES}?postingPath=true`;

  const renderHeading = () => (
    <Box css={{ display: 'flex', alignItems: 'center' }}>
      <Box css={{ flex: '1' }}>
        <Heading level={1} fontFamily="heading-alt" textStyle="heading-md">
          Select property {type === SelectPropertyConstants.LISTINGS ? '' : `for ${typeDisplay}`}
        </Heading>
      </Box>
      <TextButton
        textStyle="body-bold"
        tone="brand"
        icon={<IconPlusCircleFilled />}
        asChild
        css={{ marginLeft: '400' }}
      >
        {/* @ts-expect-error - ROUTES.PROPERTIES is not yet part of next.js paths manifest */}
        <Link href={propertyPath}>Add a property</Link>
      </TextButton>
    </Box>
  );

  const renderSubTitle = () => {
    const defaultDisplay = 'Select the property you would like to manage';
    switch (type) {
      case SelectPropertyConstants.LISTINGS:
        return defaultDisplay;
      default:
        return defaultDisplay + ` ${type} for`;
    }
  };

  const SKELETON_CARD_KEYS = ['card-0', 'card-1', 'card-2', 'card-3', 'card-4', 'card-5'];

  return (
    <Page.Root data-testid="SelectPropertyPage" css={{ backgroundColor: 'bg.neutral' }}>
      <Page.Content>
        <Box css={{ marginBottom: '800' }}>{renderHeading()}</Box>
        <Box css={{ display: 'flex', alignItems: 'center', marginBottom: '600' }} className="SubTitle">
          <Box css={{ flex: '1' }}>
            <Text data-test="SelectPropertyPage-SubTitle">{renderSubTitle()}</Text>
          </Box>
        </Box>

        <SubHeadingSection
          isLoading={isLoading}
          isLoadingForSearchbox={false}
          currentSearchText={searchText}
          filterOptions={filterOptions}
          currentFilterOptionId={filterTab}
          handleFilterTabClick={handleFilterTabClick}
          handleSearch={handleSearch}
        />

        {isLoading ? (
          <Box
            css={{
              display: 'grid',
              gap: '12px 24px',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gridAutoRows: '1fr',
            }}
          >
            {(propertyList.length > 0 ? propertyList.map((l) => l.listingAlias) : SKELETON_CARD_KEYS).map((key) => (
              <PropertyCardSkeleton key={key} />
            ))}
          </Box>
        ) : (
          renderPropertyList()
        )}
        {!isLoading && renderLoadMoreBtn()}
      </Page.Content>
    </Page.Root>
  );
};

export default SelectPropertyPage;
